import Dialog from "../../../General/ConfirmableDialog";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import { Dayjs } from "dayjs";
import { functionAppUrlVariable, generateMaintenanceReportFunctionKey } from "../../../../api/apiVariable";
import { useState } from "react";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";

const generateMaintenanceHistoryPdf = async (
  token: string | null | undefined,
  id: string | undefined,
  assetType: string,
  startDate: Dayjs | null,
  endDate: Dayjs | null
) => {
  try {
    const response = await axios.get(
      `${functionAppUrlVariable}/generateMaintenanceReport/${id}/${assetType}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate: startDate?.format("YYYY-MM-DD"),
          endDate: endDate?.format("YYYY-MM-DD"),
          code: generateMaintenanceReportFunctionKey
        },
        responseType: "blob",
      }
    );
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `MaintenanceReport_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }

    return response.data;
  } catch (error) {
    throw error;
  }
};

const GenerateMaintenanceButton = ({
  id,
  token,
  assetType,
}: {
  id: string | undefined;
  token: string | null | undefined;
  assetType: string;
}) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSnackbarClose = () => setStatus("idle");

  const handleGenerateMaintenancePdf = async () => {
    if (!startDate || !endDate) {
      alert("Please select a date range.");
      return;
    }
    setLoading(true);
    try {
      await generateMaintenanceHistoryPdf(
        token,
        id,
        assetType,
        startDate,
        endDate
      );
      setStatus("success");
      setIsDateRangePickerOpen(false);
    } catch (error: any) {
      setStatus("error");
      if (error.response && error.response.status === 500) {
        setErrorMessage("No maintenance history found for this asset");
      } else {
        setErrorMessage(error.response?.data || "Failed to generate PDF");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setIsDateRangePickerOpen(true)}
        sx={{
          mr: 2,
        }}
      >
        Generate Maintenance History PDF
      </Button>
      <Dialog
        open={isDateRangePickerOpen}
        onClose={() => setIsDateRangePickerOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                }}
                slotProps={{ textField: { variant: "outlined" } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => {
                  setEndDate(newValue);
                }}
                slotProps={{ textField: { variant: "outlined" } }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDateRangePickerOpen(false)}
            color="secondary"
            disabled={loading}
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateMaintenancePdf}
            color="primary"
            disabled={loading}
            variant="contained"
          >
            {loading ? <CircularProgress size={24} /> : "Generate PDF"}
          </Button>
        </DialogActions>
      </Dialog>
      <StatusSnackbar
        status={status}
        successMessage={"Maintenance history PDF generated successfully"}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default GenerateMaintenanceButton;
